/**
 * PreviewHighlightingDialog — UI for configuring AutoCAD-like selection
 * preview highlighting. Exposes the four AutoCAD system variables that
 * govern how entities highlight when the crosshairs hover over them:
 *
 *   - SELECTIONPREVIEW (0/1/2/3)  — when preview is enabled
 *   - PREVIEWEFFECT    (0/1/2)    — dashed / thicken / both
 *   - PREVIEWCOLOR     (#RRGGBB)  — highlight color override
 *   - PREVIEWFILTER    (bitmask)  — exclude locked layers / xrefs / ...
 *
 * Preference state lives in `usePreviewPreferences()` (persisted to
 * `localStorage`). The dialog itself is a pure controlled component —
 * `open` and `onClose` come from the parent (`SessionShellCard`). When
 * `open === false` the dialog returns `null` so it leaves no DOM nodes.
 *
 * The user may type a partial / invalid hex color in the text input
 * without it being committed to the store — we hold a local draft value
 * while the input contains something that fails `isValidHexColor`, and
 * only push the committed value once the draft becomes valid again. The
 * swatch always reflects the committed store value so the user has a
 * stable reference for "what is actually in effect right now".
 */

import { useEffect, useState } from "react";

import { useT } from "../i18n";
import type { MessageKey } from "../i18n/messages";
import {
  DEFAULT_PREVIEW_PREFERENCES,
  PREVIEW_FILTER_GROUPS,
  PREVIEW_FILTER_HATCHES,
  PREVIEW_FILTER_LOCKED_LAYER,
  PREVIEW_FILTER_MLINES,
  PREVIEW_FILTER_TABLES,
  PREVIEW_FILTER_XREFS,
  isValidHexColor,
  usePreviewPreferences,
  type PreviewEffect,
  type SelectionPreviewMode,
} from "../state/previewPreferences";

/* ── Props ─────────────────────────────────────────────────────────── */

export interface PreviewHighlightingDialogProps {
  open: boolean;
  onClose: () => void;
}

/* ── Option tables (kept at module scope so React never rebuilds them) */

const SELECTION_PREVIEW_OPTIONS: Array<{
  value: SelectionPreviewMode;
  labelKey: MessageKey;
}> = [
  { value: 0, labelKey: "settings.preview.selection.0" },
  { value: 1, labelKey: "settings.preview.selection.1" },
  { value: 2, labelKey: "settings.preview.selection.2" },
  { value: 3, labelKey: "settings.preview.selection.3" },
];

const PREVIEW_EFFECT_OPTIONS: Array<{
  value: PreviewEffect;
  labelKey: MessageKey;
}> = [
  { value: 0, labelKey: "settings.preview.effect.0" },
  { value: 1, labelKey: "settings.preview.effect.1" },
  { value: 2, labelKey: "settings.preview.effect.2" },
];

const PREVIEW_FILTER_OPTIONS: Array<{
  bit: number;
  labelKey: MessageKey;
  testSuffix: string;
}> = [
  {
    bit: PREVIEW_FILTER_LOCKED_LAYER,
    labelKey: "settings.preview.filter.locked_layer",
    testSuffix: "locked_layer",
  },
  {
    bit: PREVIEW_FILTER_XREFS,
    labelKey: "settings.preview.filter.xrefs",
    testSuffix: "xrefs",
  },
  {
    bit: PREVIEW_FILTER_TABLES,
    labelKey: "settings.preview.filter.tables",
    testSuffix: "tables",
  },
  {
    bit: PREVIEW_FILTER_MLINES,
    labelKey: "settings.preview.filter.mlines",
    testSuffix: "mlines",
  },
  {
    bit: PREVIEW_FILTER_HATCHES,
    labelKey: "settings.preview.filter.hatches",
    testSuffix: "hatches",
  },
  {
    bit: PREVIEW_FILTER_GROUPS,
    labelKey: "settings.preview.filter.groups",
    testSuffix: "groups",
  },
];

/* ── Component ─────────────────────────────────────────────────────── */

export function PreviewHighlightingDialog({
  open,
  onClose,
}: PreviewHighlightingDialogProps) {
  const t = useT();
  const prefs = usePreviewPreferences();

  // Local draft for the color text input. We need this because the user
  // may type partial garbage ("#12" → "#123f") before the value becomes
  // a valid 6-digit hex — during those intermediate states the store
  // keeps the last valid value and the <input> shows the unparseable
  // draft.
  const [colorDraft, setColorDraft] = useState<string>(prefs.previewColor);

  // When the committed color changes (from Reset, or another consumer),
  // sync the local draft so the input reflects the store.
  useEffect(() => {
    setColorDraft(prefs.previewColor);
  }, [prefs.previewColor]);

  // Only render when open — prevents the dialog leaking focus / DOM
  // when the parent is collapsed.
  if (!open) return null;

  const handleColorChange = (value: string) => {
    setColorDraft(value);
    if (isValidHexColor(value)) {
      prefs.setPreviewColor(value);
    }
  };

  const handleColorReset = () => {
    prefs.setPreviewColor(DEFAULT_PREVIEW_PREFERENCES.previewColor);
    setColorDraft(DEFAULT_PREVIEW_PREFERENCES.previewColor);
  };

  const handleResetAll = () => {
    prefs.reset();
    setColorDraft(DEFAULT_PREVIEW_PREFERENCES.previewColor);
  };

  return (
    <div
      className="dimstyle-editor-overlay"
      data-testid="preview-dialog-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <article
        className="surface-card dimstyle-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="preview-dialog-title"
        data-testid="preview-dialog"
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h2 id="preview-dialog-title" style={{ margin: 0 }}>
            {t("settings.preview.title")}
          </h2>
          <button
            type="button"
            className="ghost-button"
            onClick={onClose}
            aria-label={t("dialog.close")}
            data-testid="preview-dialog-close"
            style={{ fontSize: 20, lineHeight: 1, padding: "2px 10px" }}
          >
            ×
          </button>
        </header>

        {/* SELECTIONPREVIEW */}
        <section style={{ marginBottom: 16 }}>
          <label
            htmlFor="preview-dialog-selection"
            style={{ display: "block", marginBottom: 4 }}
          >
            {t("settings.preview.selection.label")}
          </label>
          <select
            id="preview-dialog-selection"
            className="context-select"
            data-testid="preview-dialog-selection-select"
            value={prefs.selectionPreview}
            onChange={(event) =>
              prefs.setSelectionPreview(
                Number(event.target.value) as SelectionPreviewMode,
              )
            }
          >
            {SELECTION_PREVIEW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </select>
        </section>

        {/* PREVIEWEFFECT — radio group */}
        <section style={{ marginBottom: 16 }}>
          <fieldset
            style={{
              border: "1px solid #3a3a3a",
              borderRadius: 4,
              padding: "8px 12px",
            }}
          >
            <legend>{t("settings.preview.effect.label")}</legend>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {PREVIEW_EFFECT_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <input
                    type="radio"
                    name="preview-dialog-effect"
                    data-testid={`preview-dialog-effect-${option.value}`}
                    checked={prefs.previewEffect === option.value}
                    onChange={() => prefs.setPreviewEffect(option.value)}
                  />
                  {t(option.labelKey)}
                </label>
              ))}
            </div>
          </fieldset>
        </section>

        {/* PREVIEWCOLOR */}
        <section style={{ marginBottom: 16 }}>
          <label
            htmlFor="preview-dialog-color"
            style={{ display: "block", marginBottom: 4 }}
          >
            {t("settings.preview.color.label")}
          </label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              id="preview-dialog-color"
              type="text"
              className="input"
              data-testid="preview-dialog-color-input"
              pattern="^#[0-9A-Fa-f]{6}$"
              value={colorDraft}
              onChange={(event) => handleColorChange(event.target.value)}
              style={{ width: 100 }}
            />
            <span
              className="color-swatch"
              data-testid="preview-dialog-color-swatch"
              aria-label="color swatch"
              style={{
                background: prefs.previewColor,
                width: 24,
                height: 24,
                border: "1px solid #666",
                borderRadius: 2,
                display: "inline-block",
              }}
            />
            <button
              type="button"
              className="ghost-button"
              onClick={handleColorReset}
              data-testid="preview-dialog-color-reset"
            >
              {t("settings.preview.color.reset")}
            </button>
          </div>
        </section>

        {/* PREVIEWFILTER — 6 checkboxes (bitmask) */}
        <section style={{ marginBottom: 16 }}>
          <fieldset
            style={{
              border: "1px solid #3a3a3a",
              borderRadius: 4,
              padding: "8px 12px",
            }}
          >
            <legend>{t("settings.preview.filter.label")}</legend>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {PREVIEW_FILTER_OPTIONS.map((option) => (
                <label
                  key={option.testSuffix}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <input
                    type="checkbox"
                    data-testid={`preview-dialog-filter-${option.testSuffix}`}
                    checked={(prefs.previewFilter & option.bit) !== 0}
                    onChange={() => prefs.togglePreviewFilterBit(option.bit)}
                  />
                  {t(option.labelKey)}
                </label>
              ))}
            </div>
          </fieldset>
        </section>

        <footer
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
            marginTop: 16,
          }}
        >
          <button
            type="button"
            className="ghost-button"
            onClick={handleResetAll}
            data-testid="preview-dialog-reset-all"
          >
            {t("settings.preview.reset_all")}
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={onClose}
            data-testid="preview-dialog-ok"
          >
            {t("dialog.close")}
          </button>
        </footer>
      </article>
    </div>
  );
}
